// LeftColumn.js

import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../../Constants';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

export default function LeftColumn({
  onHasInspections,
  selectedTemplate,
  onTemplateChange,
  handleBridgingData,
  onRemoveInspectionFromMap, // <-- new
}) {
  const [allInspections, setAllInspections] = useState([]);
  const [selectedInspection, setSelectedInspection] = useState('');
  const [devicesForInspection, setDevicesForInspection] = useState([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [multiInspections, setMultiInspections] = useState([]);

  useEffect(() => {
    async function loadInsp() {
      try {
        const url = `${API_BASE_URL}/api/inspections/with-joins-listdto?page=0&size=9999`;
        const resp = await fetch(url, { credentials: 'include' });
        if (!resp.ok) throw new Error(`Load => ${resp.status}`);
        const data = await resp.json();
        const arr = Array.isArray(data.content) ? data.content : [];
        setAllInspections(arr);
      } catch (err) {
        console.error('LoadInsp =>', err);
      }
    }
    loadInsp();
  }, []);

  useEffect(() => {
    if (!selectedInspection) {
      setDevicesForInspection([]);
      setSelectedDeviceIds([]);
      return;
    }
    async function loadDevs() {
      try {
        const url = `${API_BASE_URL}/api/inspections/id/${selectedInspection}/devices`;
        const resp = await fetch(url, { credentials: 'include' });
        if (!resp.ok) throw new Error(`Devices => ${resp.status}`);
        let devs = await resp.json();
        if (!Array.isArray(devs)) devs = [];
        setDevicesForInspection(devs);
        setSelectedDeviceIds([]);
      } catch (err) {
        console.error('Load dev =>', err);
        setDevicesForInspection([]);
      }
    }
    loadDevs();
  }, [selectedInspection]);

  function handleDeviceCheck(devId, checked) {
    setSelectedDeviceIds((prev) =>
      checked ? [...prev, devId] : prev.filter((x) => x !== devId)
    );
  }

  function handleAddInspection() {
    if (!selectedInspection) {
      alert('Pick an inspection first!');
      return;
    }
    if (multiInspections.some((x) => x.inspId === selectedInspection)) {
      alert('That inspection is already added.');
      return;
    }
    const newArray = [
      ...multiInspections,
      { inspId: selectedInspection, deviceIds: [...selectedDeviceIds] },
    ];
    setMultiInspections(newArray);

    // reset the selection fields so the form "clears"
    setSelectedInspection('');
    setDevicesForInspection([]);
    setSelectedDeviceIds([]);

  }

  // *** Updated => also call parent to remove bridging data
  function handleRemoveInspection(inspId) {
    const newArray = multiInspections.filter((x) => x.inspId !== inspId);
    setMultiInspections(newArray);

    // Also tell the parent to remove from bridgingMap => so it vanishes from e-mail
    if (onRemoveInspectionFromMap) {
      onRemoveInspectionFromMap(inspId);
    }
  }

  useEffect(() => {
    if (onHasInspections) {
      onHasInspections(multiInspections.length > 0);
    }
  }, [multiInspections, onHasInspections]);

  return (
    <div className="flex flex-col space-y-4">
      {/* Filter Card */}
      <div className="bg-white border border-gray-300 rounded p-4 shadow-sm">
        <h2 className="text-md font-semibold text-gray-800 mb-2">Filter Card</h2>

        <div className="mb-2">
          <label className="block text-xs font-semibold mb-1 text-gray-700">
            Template
          </label>
          <select
            className="border border-gray-300 w-full p-1 text-sm rounded"
            value={selectedTemplate}
            onChange={(e) => onTemplateChange(e.target.value)}
          >
            <option value="">-- Select Template --</option>
            <option>Account Collections</option>
            <option>Accounts - Miscellaneous</option>
            <option>DOB - 3rd Party Signature Needed</option>
            <option>DOB - Customer signature needed</option>
            <option>Inspection Approval</option>
            <option>Inspection Filing</option>
            <option>Inspection Filing Corrections</option>
            <option>Inspection Invoice</option>
            <option>Inspection Notification Letter</option>
            <option>Inspection Repair Proposal</option>
            <option>Inspection Results</option>
            <option>Inspection Scheduled</option>
            <option>Inspections to Schedule</option>
            <option>Miscellaneous Email</option>
            <option>Proposal Document</option>
          </select>
        </div>

        <h2 className="text-md font-semibold text-gray-800 mb-2">
          Select Inspection & Devices
        </h2>

        <div>
          <label className="block text-xs font-semibold mb-1 text-gray-700">
            Inspection
          </label>
          <select
            className="border border-gray-300 w-full p-1 text-sm rounded mb-2"
            value={selectedInspection}
            onChange={(e) => setSelectedInspection(e.target.value)}
          >
            <option value="">-- Select Inspection --</option>
            {allInspections.map((insp) => (
              <option key={insp.inspectionId} value={insp.inspectionId}>
                #{insp.inspectionId} (Bldg: {insp.buildingReference || 'N/A'})
              </option>
            ))}
          </select>
        </div>

        <div className="border border-gray-200 p-2 rounded mb-2">
          <h3 className="text-sm font-semibold mb-1 text-gray-700">Select Devices</h3>
          {devicesForInspection.length === 0 ? (
            <p className="text-xs text-gray-500">No devices found.</p>
          ) : (
            devicesForInspection.map((dev) => {
              const checked = selectedDeviceIds.includes(dev.deviceId);
              return (
                <label key={dev.deviceId} className="block text-xs mb-1 text-gray-700">
                  <input
                    type="checkbox"
                    className="mr-1"
                    checked={checked}
                    onChange={(evt) => handleDeviceCheck(dev.deviceId, evt.target.checked)}
                  />
                  Device #{dev.deviceId} ({dev.status || 'N/A'})
                </label>
              );
            })
          )}
        </div>

        <button
          onClick={handleAddInspection}
          className="bg-yellow-400 hover:bg-yellow-500 text-black text-xs font-semibold px-3 py-1 rounded"
        >
          Add This Inspection
        </button>
      </div>

      {/* Bridging Tables */}
      <div className="bg-white border border-gray-300 rounded p-4 shadow-sm">
        <h2 className="text-md font-semibold text-gray-800 mb-2">Added Inspections</h2>
        {multiInspections.length === 0 ? (
          <p className="text-xs text-gray-500">
            No inspections added. Pick one above & click “Add This Inspection.”
          </p>
        ) : (
          multiInspections.map((entry) => (
            <SingleBridgingTable
              key={entry.inspId}
              inspId={entry.inspId}
              deviceIds={entry.deviceIds}
              onRemove={handleRemoveInspection}
              onBridgingData={handleBridgingData}
            />
          ))
        )}
      </div>
    </div>
  );
}
function SingleBridgingTable({ inspId, deviceIds, onRemove, onBridgingData }) {
  const [assignedDevices, setAssignedDevices] = useState([]);
  const [lastParentData, setLastParentData] = useState([]);
  const [openViolationsModal, setOpenViolationsModal] = useState(false);
  const [selectedDeviceName, setSelectedDeviceName] = useState('');
  const [openStepsModal, setOpenStepsModal] = useState(false);
  const [stepsDevice, setStepsDevice] = useState({});

  useEffect(() => {
    async function loadBridging() {
      if (!inspId) {
        setAssignedDevices([]);
        maybeCallParent([], inspId);
        return;
      }
      try {
        const url = `${API_BASE_URL}/api/inspections/id/${inspId}/devices`;
        const resp = await fetch(url, { credentials: 'include' });
        if (!resp.ok) throw new Error(`Bridging => ${resp.status}`);
        let devs = await resp.json();
        if (!Array.isArray(devs)) devs = [];

        // filter
        if (deviceIds.length > 0) {
          devs = devs.filter((d) => deviceIds.includes(d.deviceId));
        }

        const enriched = await Promise.all(
          devs.map(async (dev) => {
            try {
              const bridgingUrl = `${API_BASE_URL}/api/inspection-devices/${inspId}/${dev.deviceId}`;
              const bridgingRes = await fetch(bridgingUrl, { credentials: 'include' });
              if (bridgingRes.ok) {
                const bridgingData = await bridgingRes.json();
                dev.status = bridgingData.status ?? dev.status ?? 'N/A';
                dev.inspectedBy = bridgingData.inspectedBy ?? '';
                dev.inspectionDate = bridgingData.inspectionDate ?? '';
              }
            } catch {
              dev.status = dev.status || 'N/A';
            }

            try {
              const violUrl = `${API_BASE_URL}/api/violations/count/inspection/${inspId}/device/${dev.deviceId}`;
              const violRes = await fetch(violUrl, { credentials: 'include' });
              dev.violationCount = violRes.ok ? await violRes.json() : 0;
            } catch {
              dev.violationCount = 0;
            }
            return dev;
          })
        );

        setAssignedDevices(enriched);
        maybeCallParent(enriched, inspId);

      } catch (err) {
        console.error('Load bridging =>', err);
        setAssignedDevices([]);
        maybeCallParent([], inspId);
      }
    }
    loadBridging();

    // Only re-run if inspId or deviceIds truly changed
  }, [inspId, JSON.stringify(deviceIds)]);

  function maybeCallParent(newData, inspIdVal) {
    if (!onBridgingData) return;
    const newJson = JSON.stringify(newData);
    const oldJson = JSON.stringify(lastParentData);
    if (newJson !== oldJson) {
      onBridgingData(inspIdVal, newData);
      setLastParentData(newData);
    }
  }

  function handleOpenViolations(dev) {
    setSelectedDeviceName(`Device #${dev.deviceId}`);
    setOpenViolationsModal(true);
  }

  function handleOpenSteps(dev) {
    setStepsDevice(dev);
    setOpenStepsModal(true);
  }

  return (
    <div className="border border-gray-300 rounded p-2 mb-4 bg-white">
      <h3 className="font-semibold text-gray-800 mb-2">
        Device Details (Inspection #{inspId})
      </h3>

      {assignedDevices.length === 0 ? (
        <p className="text-sm text-gray-500">No devices found (or none selected).</p>
      ) : (
        <table className="w-full text-sm border-collapse">
          <thead className="bg-gray-100 text-xs text-gray-600 uppercase">
            <tr>
              <th className="p-2 border-b">Device ID</th>
              <th className="p-2 border-b">Current Step</th>
              <th className="p-2 border-b">Inspection Date</th>
              <th className="p-2 border-b">Inspector</th>
              <th className="p-2 border-b">Violations</th>
              <th className="p-2 border-b">View</th>
              <th className="p-2 border-b">
                <div className="flex items-center justify-between">
                  <span>Steps</span>
                  {/* 
                    The "X" => calls onRemove(inspId),
                    which triggers handleRemoveInspection in the LeftColumn,
                    which calls onRemoveInspectionFromMap in the parent => 
                    removes from bridgingMap => removes from email body
                  */}
                  <button
                    onClick={() => onRemove(inspId)}
                    className="text-red-600 hover:text-red-800 ml-2"
                    title="Remove entire table"
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {assignedDevices.map((dev) => (
              <tr key={dev.deviceId} className="border-b hover:bg-gray-50">
                <td className="p-1 text-center">{dev.deviceId ?? 'N/A'}</td>
                <td className="p-1 text-center">{dev.status || 'N/A'}</td>
                <td className="p-1 text-center">{dev.inspectionDate || 'N/A'}</td>
                <td className="p-1 text-center">{dev.inspectedBy || 'N/A'}</td>
                <td className="p-1 text-center">{dev.violationCount ?? 0}</td>
                <td className="p-1 text-center">
                  <button
                    onClick={() => handleOpenViolations(dev)}
                    className="text-blue-600 hover:text-blue-800 text-xs"
                  >
                    <VisibilityIcon fontSize="small" />
                  </button>
                </td>
                <td className="p-1 text-center">
                  <button
                    onClick={() => handleOpenSteps(dev)}
                    className="text-blue-600 hover:text-blue-800 text-xs"
                  >
                    <ArrowForwardIosIcon fontSize="small" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Violations modal */}
      {openViolationsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-md max-w-md w-full">
            <h2 className="text-lg font-bold mb-2">{selectedDeviceName} Violations</h2>
            <p className="text-sm text-gray-500">
              (Insert logic to show violation details here.)
            </p>
            <button
              onClick={() => setOpenViolationsModal(false)}
              className="mt-4 bg-gray-200 px-3 py-1 rounded text-xs"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Steps modal */}
      {openStepsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-md max-w-md w-full">
            <h2 className="text-lg font-bold mb-2">
              Edit Steps (Device #{stepsDevice.deviceId})
            </h2>
            <p className="text-sm text-gray-500">
              (Insert logic to edit the steps here.)
            </p>
            <div className="mt-4">
              <button className="bg-blue-500 text-white px-3 py-1 rounded text-xs">
                Save
              </button>
              <button
                onClick={() => setOpenStepsModal(false)}
                className="ml-2 bg-gray-300 px-3 py-1 rounded text-xs"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}